import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { ShoppingCart, MapPin, User, Store, Package, LogOut, Shield, Sprout } from "lucide-react";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (p) => location.pathname === p;

  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] h-16 bg-[hsl(var(--card))]/95 backdrop-blur border-b border-border">
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/logo-wordmark.png" alt="Farm Gate — Farm Fresh. Local Discovery." className="h-9 sm:h-10 w-auto" />
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          <Button variant={isActive("/map") ? "secondary" : "ghost"} size="sm" onClick={() => navigate("/map")} data-testid="nav-discover">
            <MapPin className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Shop Local Producers</span>
          </Button>

          <Button variant="ghost" size="sm" className="relative" onClick={() => navigate("/cart")} data-testid="nav-cart">
            <ShoppingCart className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 grid place-items-center bg-[color:var(--fg-wood-600)] text-white" data-testid="nav-cart-count">{count}</Badge>
            )}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" data-testid="nav-user-menu">
                  <User className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline max-w-[100px] truncate">{user.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 z-[1100]">
                <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/my-orders")} data-testid="nav-my-orders">
                  <Package className="h-4 w-4 mr-2" /> My Orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/sell/dashboard")} data-testid="nav-sell">
                  <Store className="h-4 w-4 mr-2" /> Seller Dashboard
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin">
                    <Shield className="h-4 w-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button size="sm" className="bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => navigate("/auth")} data-testid="nav-login">
              Sign in / Register
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
